import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./singleView/single.css";
import { toast } from "react-toastify";
import { DateRangePicker } from "rsuite";
import { DatePicker } from "rsuite";
import moment from "moment-timezone";
import { ThreeDots } from "react-loader-spinner";
import { setNotification } from "../../features/notification/notificationSlice";
import { logout, reset } from "../../features/auth/authSlice";
import { reset as invoiceReset } from "../../features/invoice/invoiceSlice";
import { reset as headerReset } from "../../features/headerText/headerTextSlice";
import { reset as appSettingsReset } from "../../features/appSettings/appSettingsSlice";
import { setHeaderText } from "../../features/headerText/headerTextSlice";
import cancelImage from "../../images/singleinvoice/cancel.svg";
import addicoplus from "../../images/singleinvoice/plus-add-icon.svg";
import { useActiveUser, useCreateTimeOffRequest, useFetchLeaveTypes, useTimeOffId } from "../../components/TimeOff/timeOffApi";
import calendar from "../../images/icons/calendar-icon.svg";

const CreateTimeOff = () => {
  const [typeOfLeave, setTypeOfLeave] = useState("");
  const [userId, setUserId] = useState("");
  const [issueDate] = useState(new Date());
  const [dateRange, setDateRange] = useState(null);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [note, setNote] = useState("");
  const [uploadDocument, setUploadDocument] = useState(null);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [leaveTypeDropdownOpen, setLeaveTypeDropdownOpen] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userIsValid, setUserIsValid] = useState(false);
  const [leaveTypeIsValid, setLeaveTypeIsValid] = useState(false);
  const [dateRangeIsValid, setDateRangeIsValid] = useState(false);
  const [startTimeIsValid, setStartTimeIsValid] = useState(false);
  const [endTimeIsValid, setEndTimeIsValid] = useState(false);
  const [documentIsValid, setDocumentIsValid] = useState(false);
  const [noteIsValid, setNoteIsValid] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [dateRangeType, setDateRangeType] = useState("range");
  const [timeList, setTimeList] = useState([]);
  const userRef = useRef(null);
  const leaveTypeRef = useRef(null);
  const dateRangeRef = useRef(null);
  const startTimeRef = useRef(null);
  const endTimeRef = useRef(null);
  const documentRef = useRef(null);
  const noteRef = useRef(null);
  const userSelectDropdownRef = useRef(null);
  const leaveTypeSelectDropdownRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { appSettings } = useSelector((state) => state.appSettings);
  let token = user ? user.token : null;

  const { data: activeUser = [], isLoading: isLoadingActiveUser, refetch: activeUserRefetch } = useActiveUser(token);
  const { data: leaveType = [], isLoading: isFetchingLeaveTypes, refetch: leaveTypeRefetch } = useFetchLeaveTypes(token);
  const { data: timeOffId = [], isLoading: isLoadingTimeOffId, refetch: refetchTimeOffId } = useTimeOffId(token);

  const createTimeOffRequestMutation = useCreateTimeOffRequest(token);

  useEffect(() => {
    try {
      dispatch(setHeaderText("Time Off"));
      dispatch(setNotification(Date.now()));
      if ((user.userType !== 1 && user.userType !== 2)) {
        setUserId(user._id);
      }
    } catch (error) {
      console.log(error);
    }
  }, [dispatch, token, user._id, user.userType]);

  const handleToggleUserDropdown = () => {
    setUserSearchTerm("");
    setUserDropdownOpen(!userDropdownOpen);
  };

  const handleUserSearchInputChange = (e) => {
    setUserSearchTerm(e.target.value);
  };

  const handleSelectUser = (selectedUserId) => {
    setUserId(selectedUserId);
    setUserDropdownOpen(false);
  };

  const filteredUsers = activeUser.filter((user) => {
    const fullName = user.first_name ? `${user.first_name} ${user.last_name}` : user.company_name || "";
    return fullName.toLowerCase().includes(userSearchTerm.toLowerCase());
  });

  const handleSelect = (value) => {
    setDateRange(value);
    if (value && value[0] && value[1] && moment(value[0]).format("X") === moment(value[1]).format("X")) {
      setDateRangeType("single");
    } else {
      setDateRangeType("range");
    }
  };

  const handleUploadDocChange = (e) => {
    const file = e.target.files[0];
    if (file && ["pdf", "doc", "docx", "msword", "xls", "xlsx", "csv"].includes(file.name.split(".").pop().toLowerCase())) {
      setUploadDocument(file);
      setDocumentIsValid(false);
    } else {
      setUploadDocument(null);
      setDocumentIsValid(true);
      toast.error("Invalid file format. Allowed formats are pdf, doc, docx, msword, xls, xlsx, csv.!!", {
        position: "bottom-right",
        autoClose: 3500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  const convertTimeToDecimal = (timeStr) => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours + minutes / 60;
  };

  const handleTimeOffSubmit = async (event) => {
    event.preventDefault();
    setUserIsValid(false);
    setLeaveTypeIsValid(false);
    setDateRangeIsValid(false);
    setStartTimeIsValid(false);
    setEndTimeIsValid(false);
    setDocumentIsValid(false);
    setNoteIsValid(false);
    const startDecimal = convertTimeToDecimal(startTime);
    const endDecimal = convertTimeToDecimal(endTime);
    if (
      typeOfLeave === "" ||
      userId === "" ||
      dateRange === null ||
      note === ""
    ) {
      if (typeOfLeave === "") {
        setLeaveTypeIsValid(true);
        leaveTypeRef.current.focus();
        return;
      } else if (userId === "") {
        setUserIsValid(true);
        userRef.current.focus();
        return;
      } else if (dateRange === null) {
        setDateRangeIsValid(true);
        dateRangeRef.current.scrollIntoView({ behavior: "smooth" });
        return;
      } else if (
        moment(dateRange[0]).tz(global.config.getTimeZone()).format("X") === moment(dateRange[1]).tz(global.config.getTimeZone()).format("X")
      ) {
        if (startTime === "") {
          setStartTimeIsValid(true);
          startTimeRef.current.focus();
          return;
        } else if (endTime === "") {
          setEndTimeIsValid(true);
          endTimeRef.current.focus();
          return;
        }
      } else if (note === "") {
        setNoteIsValid(true);
        noteRef.current.focus();
        return;
      }
      console.log("note", note);
      toast.error("Please fill all required fields!", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }
    if (moment(dateRange[0]).tz(global.config.getTimeZone()).format("X") === moment(dateRange[1]).tz(global.config.getTimeZone()).format("X")) {
      if (startTime === endTime) {
        toast.error("Start time and End time shouldn't be same!", {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        endTimeRef.current.focus();
        return;
      } else if (startDecimal >= endDecimal) {
        toast.error("Start time should be less than End time!!", {
          position: "bottom-right",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        setStartTimeIsValid(true);
        setEndTimeIsValid(true);
        endTimeRef.current.focus();
        return;
      } else if (startTime === "") {
        setStartTimeIsValid(true);
        startTimeRef.current.focus();
        return;
      } else if (endTime === "") {
        setEndTimeIsValid(true);
        endTimeRef.current.focus();
        return;
      }
    }

    const requestData = {
      type_of_leave: typeOfLeave,
      user: userId,
      start_date: dateRange[0],
      end_date: dateRange[1],
      start_time: convertTimeToDecimal(startTime),
      end_time: convertTimeToDecimal(endTime),
      note,
      appId: appSettings && appSettings.appId,
      timeZone: global.config.getTimeZone(),
    };

    const formData = new FormData();
    formData.append("timeoff_document", uploadDocument);

    for (let key in requestData) {
      formData.append(key, requestData[key]);
    }
    try {
      setIsSubmitLoading(true);
      await createTimeOffRequestMutation.mutateAsync(formData);
      toast.success("Time-Off request added successfully", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      dispatch(setNotification(Date.now()));
      navigate("/user/time-off/overview");
    } catch (error) {
      console.log(error);
      if (error.response?.data?.status === "deleted") {
        localStorage.removeItem("user");
        dispatch(logout());
        dispatch(reset());
        dispatch(invoiceReset());
        dispatch(headerReset());
        dispatch(appSettingsReset());
        toast.error('This user is deleted!', {
          position: "bottom-right",
          autoClose: 1500,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
        });
        navigate("/");
        return;
      }
      toast.error("Something went wrong", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const ranges = [
    {
      label: "today",
      value: [new Date(), new Date()],
    },
    {
      label: "yesterday",
      value: [new Date(), new Date()],
    },
  ];

  // const calculateTotalDays = (start, end) => {
  //   if (!start || !end) return 0;
  //   const startDate = moment(start).startOf('day');
  //   const endDate = moment(end).startOf('day');
  //   return endDate.diff(startDate, 'days') + 1; // inclusive
  // };

  const calculateTotalDays = (start, end) => {
    try {
      if (!start || !end) return "0 days";
  
      const startDate = moment(start).tz(global.config.getTimeZone()).startOf('day');
      const endDate = moment(end).tz(global.config.getTimeZone()).startOf('day');
      const startTime = startTimeRef.current?.value ? parseInt(startTimeRef.current.value.split(':')[0]) + (parseInt(startTimeRef.current.value.split(':')[1]) / 60) : 0;
      const endTime = endTimeRef.current?.value ? parseInt(endTimeRef.current.value.split(':')[0]) + (parseInt(endTimeRef.current.value.split(':')[1]) / 60) : 0;
  
      if (startDate.isSame(endDate)) {
        // Same day calculation with start and end times
        const startDateTime = moment(start).tz(global.config.getTimeZone()).startOf('day').add(startTime, 'hours');
        const endDateTime = moment(end).tz(global.config.getTimeZone()).startOf('day').add(endTime, 'hours');
        const duration = moment.duration(endDateTime.diff(startDateTime));
        const numberOfHours = duration.asHours();
  
        if (numberOfHours > 0) {
          const numberOfDaysDecimal = (numberOfHours / 8).toFixed(2); // Assuming 8-hour workday
          const cappedNumberOfDaysDecimal = Math.min(numberOfDaysDecimal, 1);
          return `${cappedNumberOfDaysDecimal} days`;
        }
        return "0 days";
      } else {
        // Different days calculation
        const duration = moment.duration(endDate.diff(startDate));
        const numberOfDays = Math.ceil(duration.asDays()) + 1; // Inclusive
        return `${numberOfDays} days`;
      }
    } catch (error) {
      console.error("Error calculating total days:", error);
      return "0 days";
    }
  };

  const handleClickOutsideDropdown = (event) => {
    const isClickInsideUserDropdown = userSelectDropdownRef.current && userSelectDropdownRef.current.contains(event.target);
    const isClickInsideLeaveTypeDropdown = leaveTypeSelectDropdownRef.current && leaveTypeSelectDropdownRef.current.contains(event.target);

    if (!isClickInsideUserDropdown) setUserDropdownOpen(false);
    if (!isClickInsideLeaveTypeDropdown) setLeaveTypeDropdownOpen(false);
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutsideDropdown);
    return () => document.removeEventListener("click", handleClickOutsideDropdown);
  }, []);

  return (
    <div className="s1-containauto s1-containauto-client new-exp-create new-time-off-create">
      <form onSubmit={handleTimeOffSubmit}>
        <div className="fs-head-autoinvo">
          <div className="bill-num-si">
            <Link className="invoice-num-01">{timeOffId}</Link>
          </div>
          <div className="btn-aary">
            <Link to="/user/time-off/overview" className="cancelbtn-link">
              <img src={cancelImage} alt="cancel" /> Cancel Request
            </Link>
          </div>
        </div>
        <div className="m-edit-main exp-new-top-create">
          <div className="edit-sheet-manual01">
            <div className="headder-outter-res-mainu">
              <div className="sheet-manual-header">
                <div className="form-field-client">
                  <label htmlFor="client" className="form-label">
                    Type
                  </label>

                  {isFetchingLeaveTypes == true ? (
                    <div className="outter-load-table">
                      <ThreeDots
                        height="18"
                        width="20"
                        radius="9"
                        color="#405BFF"
                        ariaLabel="three-dots-loading"
                        wrapperStyle={{}}
                        wrapperClassName=""
                        visible={true}
                      />
                    </div>
                  ) : (
                    <div
                      ref={leaveTypeSelectDropdownRef}
                      className={`cursor ${leaveTypeDropdownOpen ? "open" : ""}`}
                    >
                      <span
                        id="user-select"
                        onClick={() => setLeaveTypeDropdownOpen(!leaveTypeDropdownOpen)}
                        className={`custom-dropdown-user-new custom-dropdown-select ${leaveTypeIsValid ? "input-validation-error" : ""}`}
                        ref={leaveTypeRef}
                      >
                        {typeOfLeave ? leaveType.find((leave) => leave._id === typeOfLeave)?.leave_type : "Select"}
                      </span>
                      {leaveTypeDropdownOpen && (
                        <div className="custom-dropdown-new">
                          <ul className="leave-type-list">
                            {leaveType.map((leave) => (
                              <li
                                key={leave._id}
                                className={typeOfLeave === leave._id ? "selected-item" : ""}
                                onClick={() => {
                                  setTypeOfLeave(leave._id);
                                  setLeaveTypeDropdownOpen(false);
                                }}
                              >
                                {leave.leave_type}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  {leaveTypeIsValid && (
                    <span className="text-red-500 text-xs italic">Leave Type field Cannot be empty</span>
                  )}
                </div>
                {(user.userType === 1 || user.userType === 2) && (
                  <div className="input-field-inof input-field-inof-new input-field-creat expense-currency-create-new">
                    <label htmlFor="client" >
                      User
                    </label>
                    <div ref={userSelectDropdownRef} className={`cursor ${userDropdownOpen ? "open" : ""}`}>
                      <span
                        id="user-select"
                        onClick={handleToggleUserDropdown}
                        className={`custom-dropdown-user-new custom-dropdown-user ${userIsValid ? "input-validation-error" : ""}`}
                        ref={userRef}
                      >
                        {userId ? (
                          activeUser.find((user) => user._id === userId)?.first_name ? (
                            <>
                              {activeUser.find((user) => user._id === userId)?.first_name}{" "}
                              {activeUser.find((user) => user._id === userId)?.last_name}
                            </>
                          ) : (
                            activeUser.find((user) => user._id === userId)?.company_name || "Select User"
                          )
                        ) : (
                          "Select User"
                        )}
                      </span>
                      {userDropdownOpen && (
                        <div className="custom-dropdown-new">
                          <input
                            type="text"
                            className="search-bar"
                            placeholder="Search..."
                            value={userSearchTerm}
                            onChange={handleUserSearchInputChange}
                          />
                          <ul className="client-list">
                            {filteredUsers.map((item) => (
                              <li
                                key={item._id}
                                className={userId === item._id ? "selected-item" : ""}
                                onClick={() => handleSelectUser(item._id)}
                              >
                                {item.first_name ? `${item.first_name} ${item.last_name}` : item.company_name || ""}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    {userIsValid && (
                      <span className="text-red-500 text-xs italic">User needs to be selected</span>
                    )}
                  </div>
                )}
                <div className="date-due-manual-edm input-field-create">
                  <div className="invo-date-issue-tile also-for-dued-cal">
                    <div className="cal-icon-new">
                      <img src={calendar} alt="Calendar" />
                    </div>
                    Issue Date
                  </div>

                  <div className="calender-date-due-pic">
                    <div className="date-picker-outer date-picker-outer-all expense-create-page-date">
                      <div className="custom-picker-icon custom-picker-icon-all">
                        <svg
                          width="1em"
                          height="1em"
                          viewBox="0 0 14 14"
                          fill="currentColor"
                          aria-hidden="true"
                          focusable="false"
                          className="rs-picker-toggle-caret rs-icon"
                          aria-label="calendar"
                          data-category="legacy"
                        >
                          <path d="M1 4v8.5a.5.5 0 00.5.5H8c0-.128.049-.256.146-.354.555-.555.854-1.6.854-3.146a.5.5 0 01.621-.485l.119.03A2.623 2.623 0 0012.999 6.5V4h-12zm3-2h6V0h1v2h1.5A1.5 1.5 0 0114 3.5v3a3.623 3.623 0 01-4.015 3.603c-.064 1.245-.335 2.212-.831 2.898H12.5a.5.5 0 00.5-.5v-2a.5.5 0 011 0v2a1.5 1.5 0 01-1.5 1.5h-11a1.5 1.5 0 01-1.5-1.5v-9a1.5 1.5 0 011.5-1.5H3v-2h1v2zm5 4V5h1v1h2v1h-2v1H9V7H7v2h1v1H7v2H6v-2H4v2H3v-2H1V9h2V7H1V6h2V5h1v1h2V5h1v1h2zM6 9V7H4v2h2z"></path>
                        </svg>
                      </div>

                      <div className="custoom-icon-calender custoom-icon-calender-all non-editable-calendar">
                        <DatePicker
                          oneTap
                          format="dd/MM/yyyy"
                          ranges={ranges}
                          value={issueDate}
                          placement="auto"
                          className="invoice-filter-top-date"
                          isoWeek={appSettings.weekStartDay === "Monday"}
                          timeZone={global.config.getTimeZone()}
                          disabled
                          readOnly
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="sheed-sec-after-headem">
              <div className="mobile-revert">
                <div className="inp-sec2-with2 inp-sec2-with-client user-and-client-sec">
                  <div className="form-field-client">
                    <label htmlFor="projectName" className="form-label">
                      Start and End Date
                    </label>
                    <div className="date-picker-outer date-picker-outer-all">
                      <div className="custom-picker-icon custom-picker-icon-all">
                        <svg
                          width="1em"
                          height="1em"
                          viewBox="0 0 14 14"
                          fill="currentColor"
                          aria-hidden="true"
                          focusable="false"
                          className="rs-picker-toggle-caret rs-icon"
                          aria-label="calendar"
                          data-category="legacy"
                        >
                          <path d="M1 4v8.5a.5.5 0 00.5.5H8c0-.128.049-.256.146-.354.555-.555.854-1.6.854-3.146a.5.5 0 01.621-.485l.119.03A2.623 2.623 0 0012.999 6.5V4h-12zm3-2h6V0h1v2h1.5A1.5 1.5 0 0114 3.5v3a3.623 3.623 0 01-4.015 3.603c-.064 1.245-.335 2.212-.831 2.898H12.5a.5.5 0 00.5-.5v-2a.5.5 0 011 0v2a1.5 1.5 0 01-1.5 1.5h-11a1.5 1.5 0 01-1.5-1.5v-9a1.5 1.5 0 011.5-1.5H3v-2h1v2zm5 4V5h1v1h2v1h-2v1H9V7H7v2h1v1H7v2H6v-2H4v2H3v-2H1V9h2V7H1V6h2V5h1v1h2V5h1v1h2zM6 9V7H4v2h2z"></path>
                        </svg>
                      </div>
                      <div className="custoom-icon-calender custoom-icon-calender-all">
                        <div ref={dateRangeRef}>
                          <DateRangePicker
                            showOneCalendar
                            format="dd/MM/yyyy"
                            placeholder="From - to"
                            placement="auto"
                            ranges={ranges}
                            className="project-calender time-off-cal"
                            value={dateRange}
                            onChange={handleSelect}
                            panelStyles={{ width: "100%" }}
                            timeZone={global.config.getTimeZone()}
                            isoWeek={appSettings.weekStartDay === "Monday"}
                          />
                        </div>
                      </div>
                    </div>
                    {dateRangeIsValid && (
                      <span className="text-red-500 text-xs italic">
                        Select the range of dates you want time off
                      </span>
                    )}
                  </div>
                  {dateRangeType === "single" && (
                    <>
                      <div className="form-field-client">
                        <label htmlFor="client" className="form-label">
                          Start Time
                        </label>
                        <input
                          type="time"
                          id="start-time"
                          ref={startTimeRef}
                          value={startTime}
                          onChange={(e) => setStartTime(e.target.value)}
                          className="custom-time-input"
                        />
                        {startTimeIsValid && (
                          <span className="text-red-500 text-xs italic">
                            Please select a valid start time.
                          </span>
                        )}
                      </div>
                      <div className="form-field-client">
                        <label htmlFor="client" className="form-label">
                          End Time
                        </label>
                        <input
                          type="time"
                          id="end-time"
                          ref={endTimeRef}
                          value={endTime}
                          onChange={(e) => setEndTime(e.target.value)}
                          className="custom-time-input"
                        />
                        {endTimeIsValid && (
                          <span className="text-red-500 text-xs italic">
                            Please select a valid end time.
                          </span>
                        )}
                      </div>
                    </>
                  )}

                  <div className="form-field-client">
                    <label htmlFor="totalDays" className="form-label">
                      Total Days
                    </label>
                    <input
                      type="text"
                      id="totalDays"
                      className="custom-time-input"
                      value={calculateTotalDays(dateRange?.[0], dateRange?.[1])}
                      readOnly
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="sec-after-tab-ls-part">
              <div className="sec-withother-and-add">
                <div className="attachment-head-mobile">Upload Document</div>
                <div className="style-portion-browsetext mobile" style={{ cursor: 'pointer' }}>
                  <label className="add-item-button-end-browse">
                    <img src={addicoplus} alt="addicon" /> Browse
                    <input
                      ref={documentRef}
                      type="file"
                      id="uploadDocument"
                      name="Upload Document"
                      style={{ display: 'none' }}
                      onChange={handleUploadDocChange}
                    />
                  </label>
                  <div className="upload-file-name-br">
                    {uploadDocument &&
                      <div className="uploaded-file-item">
                        {uploadDocument.name.length > 30 ? (
                          <span
                            className="shortened-name"
                            title={uploadDocument.name}
                          >
                            {uploadDocument.name.slice(0, 30)}...
                          </span>
                        ) : (
                          uploadDocument.name
                        )}
                      </div>
                    }
                  </div>
                  {documentIsValid && (
                    <span className="text-red-500 text-xs italic">Invalid file format</span>
                  )}
                </div>

                <div className="other-secp1">
                  <div className="other-head1">Remarks</div>
                  <div className="other-head1-mobile">Remarks</div>
                  <div className="other-subhead1">
                    <textarea
                      type="text"
                      className=""
                      id="Remark"
                      name="Remark"
                      placeholder="Comment here"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      style={{ resize: "none" }}
                      ref={noteRef}
                    ></textarea>
                  </div>
                  {noteIsValid && (
                    <span className="text-red-500 text-xs italic">Remark Cannot be empty</span>
                  )}
                </div>
               

                <div className="file-add-p1-attachment">
                  <div className="attachment-head">Upload Document</div>
                  <div className="style-portion-browsetext" style={{ cursor: 'pointer' }}>
                    <label className="add-item-button-end-browse">
                      <img src={addicoplus} alt="addicon" /> Browse
                      <input
                        ref={documentRef}
                        type="file"
                        id="uploadDocument"
                        name="Upload Document"
                        style={{ display: 'none' }}
                        onChange={handleUploadDocChange}
                      />
                    </label>
                    <div className="upload-file-name-br">
                      {uploadDocument &&
                        <div className="uploaded-file-item">
                          {uploadDocument.name.length > 30 ? (
                            <span
                              className="shortened-name"
                              title={uploadDocument.name}
                            >
                              {uploadDocument.name.slice(0, 30)}...
                            </span>
                          ) : (
                            uploadDocument.name
                          )}
                        </div>
                      }
                    </div>
                    {documentIsValid && (
                      <span className="text-red-500 text-xs italic">Invalid file format</span>
                    )}
                  </div>
                </div>
              </div>

            </div>
            <div className="sec-tab-ft">
              <button
                className="largecreate-invo-btn"
                type="submit"
                disabled={isSubmitLoading}
              >
                {isSubmitLoading ? <ThreeDots
                  height="18"
                  width="20"
                  radius="9"
                  color="white"
                  ariaLabel="three-dots-loading"
                  wrapperStyle={{}}
                  wrapperClassName=""
                  visible={true}
                /> : "Create Request"}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateTimeOff;